import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { bindAll } from '@bibliocommons/utils-react';
import Context from '@bibliocommons/context';
import { checkedOutItemShape, bibShape } from '@bibliocommons/bc-prop-types';
import { PrimaryButton, TextButton } from '@bibliocommons/deprecated-base-buttons';
import { Return as ReturnIcon } from '@bibliocommons/deprecated-base-icons';

import FulfilmentModal from 'app/components/bibs/shared/TitleTransactionButton/DigitalAccessButton/CheckedOutDropdown/FulfilmentModal';
import ReturnDigitalCheckoutsModal from 'app/components/shared/ItemTransactions/components/digital-checkouts/ReturnDigitalCheckoutsModal';
import ThirdPartyReaderLink from './ThirdPartyReaderLink';
import DownloadCheckoutLink from './DownloadCheckoutLink';
import DigitalHelpLink from './DigitalHelpLink';
import QuickStartLink from './QuickStartLink';
import './CheckedOutReadingLinks.scss';

export default class CheckedOutReadingLinks extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  fulfilmentWorkFlowEnabled() {
    return this.props.provider === 'OverDriveAPI';
  }

  startDigitalMissingFormatSelection(e) {
    e.preventDefault();
    this.props.startDigitalMissingFormatSelection(this.props.metadataId);
  }

  handleReturnModalOpen() {
    this.props.modalActions.openModal(this.getModalKey('return'));
  }

  getModalKey(name) {
    return `checkedout-item-${name}-modal-${this.props.metadataId}`;
  }

  handleClick() {
    const {
      itemPosition,
      itemPositionGrouped,
      totalCount,
      modalActions,
      metadataId,
      trackCheckoutConsumeAccess
    } = this.props;

    modalActions.openModal(this.getModalKey('fulfilment'));

    if (typeof trackCheckoutConsumeAccess === 'function') {
      trackCheckoutConsumeAccess({
        bibId: metadataId,
        ui: {
          ui_content_position: itemPosition,
          ui_content_position_grouped: itemPositionGrouped,
          ui_content_position_grouped_count: totalCount
        }
      });
    }
  }

  renderFulfilmentButton() {
    if (this.fulfilmentWorkFlowEnabled()) {
      return (
        <PrimaryButton disabled={this.props.disabled} handleClick={this.handleClick} className="fulfilment-button">
          <FormattedMessage id="access_this_item" />
        </PrimaryButton>
      );
    }

    return null;
  }

  renderCheckInLink() {
    if (this.props.checkInEnabled) {
      return (
        <TextButton className="btn btn-link" handleClick={this.handleReturnModalOpen} disabled={this.props.disabled}>
          <ReturnIcon />
          <FormattedMessage id="digital_return_now" />
        </TextButton>
      );
    }

    return null;
  }

  renderDownloadLink() {
    const {
      policy,
      downloadable,
      itemPosition,
      itemPositionGrouped,
      totalCount,
      thirdPartyReaderUrl,
      format,
      downloadUrl,
      metadataId,
      trackCheckoutConsumeDownload
    } = this.props;
    const showLink =
      !this.fulfilmentWorkFlowEnabled() &&
      policy.get('supportsDownload') &&
      (downloadable || !thirdPartyReaderUrl) &&
      format &&
      downloadUrl;

    if (showLink) {
      return (
        <DownloadCheckoutLink
          className="btn btn-primary"
          downloadUrl={downloadUrl}
          itemPosition={itemPosition}
          itemPositionGrouped={itemPositionGrouped}
          totalCount={totalCount}
          format={format}
          bibId={metadataId}
          trackCheckoutConsumeDownload={trackCheckoutConsumeDownload}
        />
      );
    }

    return null;
  }

  renderHelpLink() {
    const { policy, thirdPartyReaderUrl, format, downloadUrl } = this.props;

    const supportsDownload = policy?.get('supportsDownload') ?? false;

    if (!supportsDownload) {
      return <QuickStartLink policy={policy} className="btn btn-link" withIcons />;
    }

    const showLink = supportsDownload && !thirdPartyReaderUrl && format && !downloadUrl;

    if (showLink) {
      return <DigitalHelpLink policy={policy} className="btn btn-link" />;
    }

    return null;
  }

  renderChooseFormatButton() {
    const { policy, downloadable, thirdPartyReaderUrl, format } = this.props;

    const showButton =
      !this.fulfilmentWorkFlowEnabled() &&
      policy?.get('supportsDownload') &&
      (downloadable || !thirdPartyReaderUrl) &&
      !format;

    if (showButton) {
      return (
        <PrimaryButton
          disabled={this.props.disabled}
          handleClick={this.startDigitalMissingFormatSelection}
          className="download-missing-format"
        >
          <FormattedMessage id="download" />
        </PrimaryButton>
      );
    }

    return null;
  }

  renderThirdPartyReaderLink() {
    const { thirdPartyReaderUrl } = this.props;
    if (!this.fulfilmentWorkFlowEnabled() && thirdPartyReaderUrl) {
      return <ThirdPartyReaderLink className="btn btn-link" thirdPartyReaderUrl={thirdPartyReaderUrl} withIcons />;
    }

    return null;
  }

  renderFulfilmentModal() {
    const { bib, checkoutItem, accountId, checkoutActions, modalActions } = this.props;

    return this.fulfilmentWorkFlowEnabled() ? (
      <FulfilmentModal
        bib={bib}
        modalKey={this.getModalKey('fulfilment')}
        accountId={accountId}
        checkoutItem={checkoutItem}
        checkoutActions={checkoutActions}
        modalActions={modalActions}
      />
    ) : null;
  }

  render() {
    const thirdPartyReaderLink = this.renderThirdPartyReaderLink();
    const checkInLink = this.renderCheckInLink();
    const helpLink = this.renderHelpLink();
    const displayAccessLinks = !!thirdPartyReaderLink || !!checkInLink || !!helpLink;

    return (
      <div className="cp-checked-out-reading-links">
        {this.renderFulfilmentButton()}
        {this.renderDownloadLink()}
        {this.renderChooseFormatButton()}
        {displayAccessLinks && (
          <div className="access-title-block">
            {thirdPartyReaderLink}
            {checkInLink}
            {helpLink}
          </div>
        )}
        {this.renderFulfilmentModal()}
        <ReturnDigitalCheckoutsModal
          modalKey={this.getModalKey('return')}
          modalActions={this.props.modalActions}
          bib={this.props.bib}
          shelfActions={this.props.shelfActions}
          checkIn={this.props.checkIn}
          checkoutId={this.props.checkoutId}
          metadataId={this.props.metadataId}
          accountId={this.props.accountId}
          messagingId={this.props.messagingId}
        />
      </div>
    );
  }
}

CheckedOutReadingLinks.propTypes = {
  checkInEnabled: PropTypes.bool,
  disabled: PropTypes.bool,
  itemPosition: PropTypes.number,
  itemPositionGrouped: PropTypes.number,
  totalCount: PropTypes.number,
  policy: ImmutablePropTypes.mapContains({
    thirdPartyReaderUrl: PropTypes.string,
    supportsDownload: PropTypes.bool,
    quickstartUrl: PropTypes.string,
    helpUrl: PropTypes.string
  }),
  provider: PropTypes.string,
  checkoutId: PropTypes.string.isRequired,
  metadataId: PropTypes.string.isRequired,
  accountId: PropTypes.number.isRequired,
  downloadable: PropTypes.bool,
  downloadUrl: PropTypes.string,
  format: PropTypes.string,
  checkIn: PropTypes.func.isRequired,
  startDigitalMissingFormatSelection: PropTypes.func.isRequired,
  messagingId: PropTypes.string,
  bib: bibShape.isRequired,
  checkoutItem: checkedOutItemShape.isRequired,
  checkoutActions: PropTypes.objectOf(PropTypes.func).isRequired,
  modalActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfActions: PropTypes.objectOf(PropTypes.func).isRequired,
  thirdPartyReaderUrl: PropTypes.string,
  trackCheckoutConsumeAccess: PropTypes.func,
  trackCheckoutConsumeDownload: PropTypes.func
};

CheckedOutReadingLinks.contextType = Context;
